import { clsx } from 'clsx';
import { forwardRef } from 'react';

import { AiIcon } from '@dynatrace/strato-icons';

import * as styles from './AiLoadingIndicator.sty.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';


/**
 * Accepted properties for `AiLoadingIndicator.Icon` slot.
 * @public
 */
export interface AiLoadingIndicatorIconProps
  extends StylingProps,
    DataTestId,
    WithChildren {}

/**
 * Allows consumers to specify an icon displayed in the `AiLoadingIndicator` component.
 * Defaults to the `AiIcon` if no children are provided.
 * @public
 */
export const AiLoadingIndicatorIcon = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  AiLoadingIndicatorIconProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  return (
    <div
      ref={forwardedRef}
      className={clsx(styles.iconWrapper, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      aria-hidden="true"
      {...remainingProps}
    >
      {/* default to AiIcon if slot is empty */}
      {children ?? <AiIcon />}
    </div>
  );
});

(
  AiLoadingIndicatorIcon as typeof AiLoadingIndicatorIcon & {
    displayName: string;
  }
).displayName = 'AiLoadingIndicator.Icon';
